import { __assign, __extends, __makeTemplateObject } from "tslib";
// Libraries
import React, { PureComponent, memo } from 'react';
import { css } from 'emotion';
// Components
import { Tooltip } from '../Tooltip/Tooltip';
import { TimePickerContent } from './TimeRangePicker/TimePickerContent';
import { ClickOutsideWrapper } from '../ClickOutsideWrapper/ClickOutsideWrapper';
// Utils & Services
import { stylesFactory } from '../../themes/stylesFactory';
import { withTheme, useTheme } from '../../themes/ThemeContext';
// Types
import { isDateTime, rangeUtil, dateTimeFormat, timeZoneFormatUserFriendly, dateMath, } from '@grafana/data';
import { otherOptions, quickOptions } from './rangeOptions';
import { ButtonGroup, ToolbarButton } from '../Button';
var UnthemedTimeRangePicker = /** @class */ (function (_super) {
    __extends(UnthemedTimeRangePicker, _super);
    function UnthemedTimeRangePicker() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.state = {
            isOpen: false,
        };
        _this.onChange = function (timeRange) {
            _this.props.onChange(timeRange);
            _this.setState({ isOpen: false });
        };
        _this.onOpen = function (event) {
            var isOpen = _this.state.isOpen;
            event.stopPropagation();
            event.preventDefault();
            _this.setState({ isOpen: !isOpen });
        };
        _this.onClose = function () {
            _this.setState({ isOpen: false });
        };
        return _this;
    }
    UnthemedTimeRangePicker.prototype.render = function () {
        var _a = this.props, value = _a.value, onMoveBackward = _a.onMoveBackward, onMoveForward = _a.onMoveForward, onZoom = _a.onZoom, timeZone = _a.timeZone, timeSyncButton = _a.timeSyncButton, isSynced = _a.isSynced, theme = _a.theme, history = _a.history, onChangeTimeZone = _a.onChangeTimeZone, hideQuickRanges = _a.hideQuickRanges;
        var isOpen = this.state.isOpen;
        var styles = getStyles(theme);
        var hasAbsolute = isDateTime(value.raw.from) || isDateTime(value.raw.to);
        var variant = isSynced ? 'active' : 'default';
        return (React.createElement(ButtonGroup, { className: styles.container },
            hasAbsolute && React.createElement(ToolbarButton, { variant: variant, onClick: onMoveBackward, icon: "angle-left", narrow: true }),
            React.createElement(Tooltip, { content: React.createElement(TimePickerTooltip, { timeRange: value, timeZone: timeZone }), placement: "bottom" },
                React.createElement(ToolbarButton, { "aria-label": "TimePicker Open Button", onClick: this.onOpen, icon: "clock-nine", isOpen: isOpen, variant: variant },
                    React.createElement(TimePickerButtonLabel, __assign({}, this.props)))),
            isOpen && (React.createElement(ClickOutsideWrapper, { includeButtonPress: false, onClick: this.onClose },
                React.createElement(TimePickerContent, { timeZone: timeZone, value: value, onChange: this.onChange, otherOptions: otherOptions, quickOptions: quickOptions, history: history, showHistory: true, onChangeTimeZone: onChangeTimeZone, hideQuickRanges: hideQuickRanges }))),
            timeSyncButton,
            hasAbsolute && React.createElement(ToolbarButton, { onClick: onMoveForward, icon: "angle-right", narrow: true, variant: variant }),
            React.createElement(Tooltip, { content: ZoomOutTooltip, placement: "bottom" },
                React.createElement(ToolbarButton, { onClick: onZoom, icon: "search-minus", variant: variant }))));
    };
    return UnthemedTimeRangePicker;
}(PureComponent));
export { UnthemedTimeRangePicker };
var ZoomOutTooltip = function () { return (React.createElement(React.Fragment, null,
    "Time range zoom out ",
    React.createElement("br", null),
    " CTRL+Z")); };
var TimePickerTooltip = function (_a) {
    var timeRange = _a.timeRange, timeZone = _a.timeZone;
    var theme = useTheme();
    var styles = getLabelStyles(theme);
    return (React.createElement(React.Fragment, null,
        dateTimeFormat(timeRange.from, { timeZone: timeZone }),
        React.createElement("div", { className: "text-center" }, "to"),
        dateTimeFormat(timeRange.to, { timeZone: timeZone }),
        React.createElement("div", { className: "text-center" },
            React.createElement("span", { className: styles.utc }, timeZoneFormatUserFriendly(timeZone)))));
};
export var TimePickerButtonLabel = memo(function (_a) {
    var hideText = _a.hideText, value = _a.value, timeZone = _a.timeZone;
    var theme = useTheme();
    var styles = getLabelStyles(theme);
    if (hideText) {
        return null;
    }
    return (React.createElement("span", { className: styles.container },
        React.createElement("span", null, formattedRange(value, timeZone)),
        React.createElement("span", { className: styles.utc }, rangeUtil.describeTimeRangeAbbreviation(value, timeZone))));
});
TimePickerButtonLabel.displayName = 'TimePickerButtonLabel';
var formattedRange = function (value, timeZone) {
    var adjustedTimeRange = {
        to: dateMath.isMathString(value.raw.to) ? value.raw.to : value.to,
        from: dateMath.isMathString(value.raw.from) ? value.raw.from : value.from,
    };
    return rangeUtil.describeTimeRange(adjustedTimeRange, timeZone);
};
export var TimeRangePicker = withTheme(UnthemedTimeRangePicker);
var getStyles = stylesFactory(function (theme) {
    return {
        container: css(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n      position: relative;\n      display: flex;\n      vertical-align: middle;\n    "], ["\n      position: relative;\n      display: flex;\n      vertical-align: middle;\n    "]))),
    };
});
var getLabelStyles = stylesFactory(function (theme) {
    return {
        container: css(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n      display: flex;\n      align-items: center;\n      white-space: nowrap;\n    "], ["\n      display: flex;\n      align-items: center;\n      white-space: nowrap;\n    "]))),
        utc: css(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n      color: ", ";\n      font-size: ", ";\n      padding-left: 6px;\n      line-height: 28px;\n      vertical-align: bottom;\n      font-weight: ", ";\n    "], ["\n      color: ", ";\n      font-size: ", ";\n      padding-left: 6px;\n      line-height: 28px;\n      vertical-align: bottom;\n      font-weight: ", ";\n    "])), theme.palette.orange, theme.typography.size.sm, theme.typography.weight.semibold),
    };
});
var templateObject_1, templateObject_2, templateObject_3;
//# sourceMappingURL=TimeRangePicker.js.map